import React, { useState } from 'react';
import { Button, Card, CardContent, SwipeableDrawer } from '@material-ui/core';
import Modal from 'react-modal';
import { AddCircleOutline } from '@material-ui/icons';
import { useMutation, useQueryClient } from 'react-query';
import { toast, Toaster } from 'react-hot-toast';

import { Guid, SignUpConsultantProps, User } from '@project/types';
import { adminConsultantService, adminUserService, userService } from '@project/services';
import { StringHelper } from '@project/helpers';
import {
  AddConsultantsForm,
  BasicTable,
  EditCallCenterAgentForm,
  EditCallCenterAgentFormProps,
  PaginationButtons,
  SearchField,
} from '@project/components';
import { RoleEnum } from '@project/enums';
import { useAdminConsultantData } from '@project/react-queries';

Modal.setAppElement('#root');

const AdminInvitationScreen: React.FC = () => {
  const anchorRight = 'right';
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [initialEditCallCenterAgentValues, setInitialEditCallCenterAgentValues] = useState({
    userId: '',
    extension: '',
  });
  const [searchConsultants, setSearchConsultants] = useState<string>('');
  const [pagination, setPagination] = useState<{ page: number; pageEntries: number }>({
    page: 1,
    pageEntries: 10,
  });

  const consultantData = useAdminConsultantData(
    pagination.page.toString(),
    pagination.pageEntries.toString(),
    searchConsultants,
  );
  const queryClient = useQueryClient();

  const _addConsultantFail = () => null;

  const onSuccessAddConsultant = () => {
    toast.success('Successfully added consultant');
    queryClient.invalidateQueries('consultantData');
    setIsAddOpen(false);
  };

  const useDeleteConsultantMutation = useMutation(
    'deleteConsultant',
    (id: Guid) => adminConsultantService.deleteConsultant(id),
    {
      onSuccess: () => {
        toast.success('Successfully deleted consultant');
        queryClient.invalidateQueries('consultantData');
      },
      onError: () => {
        toast.error('Error deleting consultant');
      },
    },
  );

  const useMutationDeleteExtension = useMutation(
    'deleteExtension',
    (formData: EditCallCenterAgentFormProps) => userService.deleteExtension(formData.userId),
    {
      onSuccess: (response) => {
        const updatedUser = consultantData.data.items.find((user) => user.id === response.userId);
        updatedUser.extension = '';
        queryClient.invalidateQueries([
          'consultantData',
          pagination.page.toString(),
          pagination.pageEntries.toString(),
          searchConsultants,
        ]);
        toast.success('Successfully deleted extension');
      },
    },
  );

  const useMutationUpsertExtension = useMutation(
    'upsertExtension',
    (formData: EditCallCenterAgentFormProps) =>
      userService.upsertExtension(formData.userId, formData.extension),
    {
      onSuccess: (response) => {
        const updatedUser = consultantData.data.items.find((user) => user.id === response.userId);
        updatedUser.extension = response.extension;
        queryClient.invalidateQueries([
          'consultantData',
          pagination.page.toString(),
          pagination.pageEntries.toString(),
          searchConsultants,
        ]);
        toast.success('Successfully updated extension');
      },
    },
  );

  const onSubmitFormEditCallCenterAgentForm = (formData: EditCallCenterAgentFormProps) => {
    if (StringHelper.isNullOrEmpty(formData.extension)) {
      return useMutationDeleteExtension.mutateAsync(formData);
    }
    return useMutationUpsertExtension.mutateAsync(formData);
  };

  const signUpConsultantInitialValues: SignUpConsultantProps = {
    firstName: '',
    lastName: '',
    email: '',
    cellPhoneNumber: '',
    role: 'Consultant',
  };

  const _signUpConsultantSubmission = (formData: SignUpConsultantProps) => {
    return adminUserService.signUpConsultant(formData);
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'firstName',
    },
    {
      Header: 'Surname',
      accessor: 'lastName',
    },
    {
      Header: 'Email',
      accessor: 'email',
    },
    {
      Header: 'PhoneNumber',
      accessor: 'phoneNumber',
    },
    {
      Header: 'Roles',
      accessor: 'roles',
    },
  ];

  const usersTableCard = () => {
    return (
      <div className="flex flex-col flex-grow mx-8 mt-2">
        <Card className="flex flex-grow shadow-2xl">
          <CardContent className="flex flex-col flex-grow">
            {!consultantData.isLoading && (
              <BasicTable
                tableData={consultantData.data.items.map((user) => ({
                  ...user,
                  isEditable: RoleEnum[user.roles] === RoleEnum.CallCenterAgent,
                }))}
                tableColumns={columns}
                path="/admin/individual/user"
                key={searchConsultants + consultantData.data.totalCount}
                isViewable={false}
                isDeletable
                onDelete={({ id }) => useDeleteConsultantMutation.mutateAsync(id)}
                isEditable
                onEdit={(user: User) => {
                  const isExtensionNew = !!(user.extension === null || user.extension === '');
                  setInitialEditCallCenterAgentValues({
                    userId: user.id,
                    extension: isExtensionNew ? '' : user.extension,
                  });
                  setIsEditOpen(true);
                }}
              />
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <>
      <Toaster />
      <div className="flex flex-row justify-between">
        <div className="mt-2 flex justify-start mx-8 text-xl">Consultant</div>
        <Button
          className="mt-2 flex bg-tertiary text-white hover:bg-tertiary-dark mr-24"
          size="small"
          startIcon={<AddCircleOutline />}
          onClick={() => setIsAddOpen(true)}
        >
          Add New
        </Button>
      </div>

      <div className="border-b border-black mt-2 flex justify-start mx-8" />
      <div className="flex justify-start ml-8 mt-2">
        <SearchField
          placeholder="Search By Email"
          value={searchConsultants}
          isLoading={consultantData.isLoading}
          onChange={(value) => {
            setSearchConsultants(value);
            setPagination({ ...pagination, page: 1 });
          }}
        />
      </div>
      {usersTableCard()}
      {!consultantData.isLoading && (
        <>
          <PaginationButtons
            page={pagination.page}
            setPage={(newPage, newPageEntries) =>
              setPagination({ page: newPage, pageEntries: newPageEntries })
            }
            pageIndex={consultantData?.data?.pageIndex}
            totalPages={consultantData?.data?.totalPages}
            pageEntries={pagination.pageEntries}
          />
          <SwipeableDrawer
            anchor={anchorRight}
            open={isAddOpen}
            onClose={() => setIsAddOpen(false)}
            onOpen={() => setIsAddOpen(true)}
            className="w-96"
          >
            <AddConsultantsForm
              initialValues={signUpConsultantInitialValues}
              submitForm={_signUpConsultantSubmission}
              onSuccess={() => {
                onSuccessAddConsultant();
              }}
              onFailure={_addConsultantFail}
            />
          </SwipeableDrawer>
          <SwipeableDrawer
            anchor={anchorRight}
            open={isEditOpen}
            onClose={() => setIsEditOpen(false)}
            onOpen={() => setIsEditOpen(true)}
            className="w-96"
          >
            <EditCallCenterAgentForm
              initialValues={initialEditCallCenterAgentValues}
              submitForm={onSubmitFormEditCallCenterAgentForm}
              onSuccess={() => {
                setIsEditOpen(false);
              }}
              onFailure={() => null}
            />
          </SwipeableDrawer>
        </>
      )}
    </>
  );
};
export default AdminInvitationScreen;
